import React, { ReactNode } from 'react'
import { Platform } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { HStack } from '../hstack'
import { Text } from '../text'
import { TextWithLinks } from '../text-with-links'
import { VStack } from '../vstack'

const LABEL_DIVIDER = '|'

// Figma models the feed-out message as a web/mobile pair — FeedOutMessage
// (9079:56499) and Mobile/Comment (11574:153346) — down to a radius variable
// each (`message-web` 16, `message-mobile` 8). The label row differs too: web
// names the recipient in default ink at P2 next to a `border/subtle` rule,
// mobile mutes the whole line to P3 and separates it with a glyph.
const isWeb = Platform.OS === 'web'
const labelSize = isWeb ? 'body' : 'P3'
const bodySize = isWeb ? 'body' : 'P3'

export type MessageProps = {
    author?: string
    timestamp?: string
    /** Slot rendered at the left end of the top bar (e.g. an automatic/manual feed icon). */
    leading?: ReactNode
    /** Slot rendered at the right end of the top bar (e.g. notification-channel icons). */
    topBarEnd?: ReactNode
    /** Overrides the bubble fill for variants the palette does not model yet. */
    backgroundColor?: string
    children: ReactNode
    className?: string
    testID?: string
}

export const Message: React.FC<MessageProps> = ({
    author,
    timestamp,
    leading,
    topBarEnd,
    backgroundColor,
    children,
    className,
    testID,
}) => (
    <VStack testID={testID} className={twMerge('w-full items-end gap-4', className)}>
        {author || timestamp || leading || topBarEnd ? (
            <HStack className='w-full items-center justify-between'>
                <HStack
                    className={twMerge(
                        'flex-1 min-w-0 shrink items-center',
                        isWeb ? 'gap-2' : 'gap-1',
                    )}
                >
                    {leading}
                    {author ? (
                        <Text
                            size={labelSize}
                            tone={isWeb ? 'default' : 'muted'}
                            className='min-w-0 shrink'
                        >
                            {author}
                        </Text>
                    ) : null}
                    {author && timestamp ? (
                        isWeb ? (
                            <Box className='w-px self-stretch bg-border-subtle' />
                        ) : (
                            <Text size={labelSize} tone='muted' className='shrink-0'>
                                {LABEL_DIVIDER}
                            </Text>
                        )
                    ) : null}
                    {timestamp ? (
                        <Text size={labelSize} tone='muted' numberOfLines={1} className='shrink-0'>
                            {timestamp}
                        </Text>
                    ) : null}
                </HStack>
                {topBarEnd}
            </HStack>
        ) : null}
        <Box
            className={twMerge(
                'w-full border border-solid border-aos-primary-border bg-aos-primary-surface',
                isWeb ? 'rounded-2xl p-4' : 'rounded-lg p-3',
            )}
            style={backgroundColor ? { backgroundColor } : undefined}
        >
            {typeof children === 'string' ? (
                <TextWithLinks
                    size={bodySize}
                    tone='default'
                    className='w-full whitespace-pre-line break-words'
                    text={children}
                />
            ) : (
                children
            )}
        </Box>
    </VStack>
)

Message.displayName = 'Message'
